import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Package, MagnifyingGlass, MapPin, Clock, ArrowRight, ArrowSquareOut, CaretDown, Airplane, CheckCircle, Warning, Info, XCircle, EnvelopeSimple, BellRinging } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  delivered: { label: 'Delivered', color: '#16A34A', bg: '#F0FDF4', icon: CheckCircle },
  in_transit: { label: 'In Transit', color: '#2563EB', bg: '#EFF6FF', icon: Airplane },
  intransit: { label: 'In Transit', color: '#2563EB', bg: '#EFF6FF', icon: Airplane },
  out_for_delivery: { label: 'Out for Delivery', color: '#D97706', bg: '#FFFBEB', icon: Airplane },
  pre_transit: { label: 'Pre-Transit', color: '#6B7280', bg: '#F9FAFB', icon: Package },
  transit: { label: 'In Transit', color: '#2563EB', bg: '#EFF6FF', icon: Airplane },
  pickup: { label: 'Picked Up', color: '#2563EB', bg: '#EFF6FF', icon: Package },
  expired: { label: 'Expired', color: '#EF4444', bg: '#FEF2F2', icon: Warning },
  notfound: { label: 'Not Found', color: '#6B7280', bg: '#F9FAFB', icon: Info },
  pending: { label: 'Pending', color: '#6B7280', bg: '#F9FAFB', icon: Clock },
  detected: { label: 'Carrier Detected', color: '#8B5CF6', bg: '#F5F3FF', icon: MagnifyingGlass },
  unknown: { label: 'Unknown', color: '#6B7280', bg: '#F9FAFB', icon: Info },
  intake_miami: { label: 'Received in Miami', color: '#2563EB', bg: '#EFF6FF', icon: MapPin },
  arrived_san_jose: { label: 'Arrived San Jose', color: '#0D9488', bg: '#F0FDFA', icon: MapPin },
  ready_for_pickup: { label: 'Ready for Pickup', color: '#D97706', bg: '#FFFBEB', icon: Package },
};

const EXAMPLE_FORMATS = [
  { carrier: 'UPS', example: '1Z999AA10123456784', color: '#351C15' },
  { carrier: 'USPS', example: '9400111899223100001', color: '#004B87' },
  { carrier: 'FedEx', example: '123456789012', color: '#4D148C' },
  { carrier: 'DHL', example: '1234567890', color: '#D4A017' },
  { carrier: 'Amazon', example: 'TBA123456789012', color: '#FF9900' },
];

export default function TrackAnyPackage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [trackingNumber, setTrackingNumber] = useState(searchParams.get('q') || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  useEffect(() => {
    if (searchParams.get('q')) {
      handleTrack();
    }
  }, []); // eslint-disable-line

  const handleTrack = async (e) => {
    e?.preventDefault();
    const num = trackingNumber.trim();
    if (!num) {
      toast.error('Please enter a tracking number');
      return;
    }
    setLoading(true);
    setSearched(true);
    setResult(null);
    try {
      const resp = await axios.post(`${API_URL}/api/universal-track`, { tracking_number: num });
      setResult(resp.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to look up tracking number');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const key = (status || 'unknown').toLowerCase().replace(/ /g, '_');
    return STATUS_CONFIG[key] || STATUS_CONFIG.unknown;
  };

  return (
    <div className="min-h-screen bg-[#0F172A]" data-testid="track-any-package-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-[#0F172A]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 group" data-testid="nav-logo">
            <img src="/assets/trackmypackage-logo.png" alt="Track My Package" className="h-10" />
          </button>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')} className="text-sm text-gray-400 hover:text-white transition-colors" data-testid="nav-home">Home</button>
            <Button onClick={() => navigate('/login')} variant="ghost" className="text-sm text-gray-400 hover:text-white" data-testid="nav-login">Log In</Button>
          </div>
        </div>
      </nav>

      {/* Hero + Search */}
      <section className="pt-16 pb-8 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight">
              Track <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">Any Package</span>
            </h1>
            <p className="text-gray-400 mt-4 text-base sm:text-lg max-w-xl mx-auto">
              Enter any tracking number from UPS, USPS, FedEx, DHL, and more. We'll identify the carrier and show you the latest status.
            </p>
          </motion.div>

          {/* Search */}
          <motion.form
            onSubmit={handleTrack}
            className="mt-10 flex gap-2 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="relative flex-1">
              <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
              <Input
                value={trackingNumber}
                onChange={e => setTrackingNumber(e.target.value.toUpperCase())}
                placeholder="Enter tracking number..."
                className="pl-12 h-14 rounded-xl text-base font-mono bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-cyan-500 focus:ring-cyan-500/20"
                data-testid="universal-tracking-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="h-14 px-8 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold text-base shadow-lg shadow-cyan-500/20"
              data-testid="universal-track-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>Track</>
              )}
            </Button>
          </motion.form>

          {/* Example Formats */}
          {!searched && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mt-8 flex flex-wrap justify-center gap-3"
            >
              {EXAMPLE_FORMATS.map(f => (
                <button
                  key={f.carrier}
                  onClick={() => { setTrackingNumber(f.example); }}
                  className="group flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:border-cyan-500/40 transition-all"
                  data-testid={`example-${f.carrier.toLowerCase()}`}
                >
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: f.color }} />
                  <span className="text-xs text-gray-400 group-hover:text-gray-200">{f.carrier}</span>
                  <span className="text-xs text-gray-600 font-mono">{f.example.slice(0, 8)}...</span>
                </button>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* Results */}
      <section className="px-6 pb-20">
        <div className="max-w-3xl mx-auto">
          <AnimatePresence mode="wait">
            {loading && (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center py-16 gap-4"
              >
                <div className="w-12 h-12 border-3 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-gray-400 text-sm">Looking up tracking number...</p>
              </motion.div>
            )}

            {!loading && searched && result && (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                {/* Carrier + Status Card */}
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden" data-testid="tracking-result-card">
                  {/* Header */}
                  <div className="p-6 border-b border-white/5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-3">
                        {result.carrier && (
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: result.carrier.color + '20' }}>
                            <Airplane size={20} style={{ color: result.carrier.color === '#FFCC00' ? '#D4A017' : result.carrier.color }} />
                          </div>
                        )}
                        <div>
                          <p className="text-white font-semibold text-lg" data-testid="carrier-name">
                            {result.carrier?.name || 'Unknown Carrier'}
                          </p>
                          <p className="text-gray-500 text-sm font-mono" data-testid="tracking-number-display">{result.tracking_number}</p>
                        </div>
                      </div>
                      {(() => {
                        const si = getStatusInfo(result.status);
                        const Icon = si.icon;
                        return (
                          <div
                            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium"
                            style={{ backgroundColor: si.bg, color: si.color }}
                            data-testid="tracking-status-badge"
                          >
                            <Icon size={16} weight="fill" />
                            {result.status_description || si.label}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Extra info row */}
                    <div className="mt-4 flex flex-wrap gap-6 text-sm">
                      {result.estimated_delivery && (
                        <div className="flex items-center gap-2">
                          <Clock size={14} className="text-gray-500" />
                          <span className="text-gray-400">Est. delivery:</span>
                          <span className="text-white">{result.estimated_delivery}</span>
                        </div>
                      )}
                      {result.origin && (
                        <div className="flex items-center gap-2">
                          <MapPin size={14} className="text-gray-500" />
                          <span className="text-gray-400">From:</span>
                          <span className="text-white">{result.origin}</span>
                        </div>
                      )}
                      {result.destination && (
                        <div className="flex items-center gap-2">
                          <MapPin size={14} className="text-gray-500" />
                          <span className="text-gray-400">To:</span>
                          <span className="text-white">{result.destination}</span>
                        </div>
                      )}
                      {result.service && (
                        <div className="flex items-center gap-2">
                          <Package size={14} className="text-gray-500" />
                          <span className="text-gray-400">Service:</span>
                          <span className="text-white">{result.service}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Internal package details (if from our system) */}
                  {result.source === 'internal' && result.internal_data && (
                    <div className="p-6 border-b border-white/5 bg-cyan-500/5">
                      <p className="text-cyan-400 text-xs uppercase tracking-wide font-semibold mb-3">Package Details (Track My Package)</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {result.internal_data.description && (
                          <div>
                            <p className="text-gray-500 text-xs">Description</p>
                            <p className="text-white text-sm">{result.internal_data.description}</p>
                          </div>
                        )}
                        {result.internal_data.weight_lbs > 0 && (
                          <div>
                            <p className="text-gray-500 text-xs">Weight</p>
                            <p className="text-white text-sm">{result.internal_data.weight_lbs} lbs</p>
                          </div>
                        )}
                        {result.internal_data.shipping_cost > 0 && (
                          <div>
                            <p className="text-gray-500 text-xs">Shipping Cost</p>
                            <p className="text-cyan-400 text-sm font-semibold">${result.internal_data.shipping_cost}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Events Timeline */}
                  {result.events && result.events.length > 0 && (
                    <div className="p-6">
                      <p className="text-gray-400 text-xs uppercase tracking-wide font-semibold mb-4">Tracking History</p>
                      <div className="space-y-0">
                        {result.events.map((evt, i) => (
                          <div key={i} className="flex gap-4" data-testid={`tracking-event-${i}`}>
                            {/* Timeline line */}
                            <div className="flex flex-col items-center">
                              <div className={`w-3 h-3 rounded-full mt-1.5 ${i === 0 ? 'bg-cyan-500 ring-4 ring-cyan-500/20' : 'bg-gray-600'}`} />
                              {i < result.events.length - 1 && (
                                <div className="w-px flex-1 bg-white/10 my-1" />
                              )}
                            </div>
                            {/* Content */}
                            <div className="pb-5 flex-1">
                              <p className={`text-sm font-medium ${i === 0 ? 'text-white' : 'text-gray-400'}`}>
                                {evt.description}
                              </p>
                              <div className="flex flex-wrap gap-3 mt-1">
                                {evt.date && <span className="text-xs text-gray-500">{evt.date}</span>}
                                {evt.location && <span className="text-xs text-gray-500">{evt.location}</span>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Carrier link */}
                  {result.carrier?.track_url && (
                    <div className="px-6 pb-6">
                      <a
                        href={result.carrier.track_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-white/5 border border-white/10 text-gray-300 hover:text-white hover:border-cyan-500/40 transition-all text-sm"
                        data-testid="track-on-carrier-link"
                      >
                        Track on {result.carrier.name} website
                        <ArrowSquareOut size={16} />
                      </a>
                    </div>
                  )}
                </div>

                {/* Not found message */}
                {!result.found && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex items-start gap-3" data-testid="tracking-not-found">
                    <Warning size={20} className="text-amber-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-amber-400 text-sm font-medium">Carrier not recognized</p>
                      <p className="text-gray-400 text-xs mt-1">
                        We couldn't identify the carrier for this tracking number. Please verify the number and try again.
                      </p>
                    </div>
                  </div>
                )}

              </motion.div>
            )}

            {!loading && searched && !result && (
              <motion.div
                key="error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-16"
              >
                <XCircle size={48} className="mx-auto text-red-400 mb-4" />
                <p className="text-white text-lg">Something went wrong</p>
                <p className="text-gray-400 text-sm mt-2">Please try again or check the tracking number.</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Supported Carriers */}
      {!searched && (
        <section className="px-6 pb-20">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <h2 className="text-center text-lg font-semibold text-white mb-8">Supported Carriers</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="supported-carriers-grid">
                {[
                  { name: 'UPS', color: '#351C15' },
                  { name: 'USPS', color: '#004B87' },
                  { name: 'FedEx', color: '#4D148C' },
                  { name: 'DHL', color: '#D4A017' },
                  { name: 'Amazon', color: '#FF9900' },
                  { name: 'OnTrac', color: '#00529B' },
                  { name: 'Canada Post', color: '#DA291C' },
                  { name: 'Royal Mail', color: '#D4351C' },
                  { name: 'China Post', color: '#006633' },
                  { name: 'Yanwen', color: '#0055A5' },
                  { name: 'LaserShip', color: '#E31837' },
                  { name: 'Purolator', color: '#C41230' },
                ].map(c => (
                  <div
                    key={c.name}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 border border-white/5"
                  >
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
                    <span className="text-sm text-gray-300">{c.name}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-md flex items-center justify-center">
              <Package size={12} className="text-white" weight="fill" />
            </div>
            <span className="text-gray-500 text-sm">Track My Package</span>
          </div>
          <p className="text-gray-600 text-xs">Universal package tracking. Data sourced from carrier APIs.</p>
        </div>
      </footer>
    </div>
  );
}
